import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Account } from "../../../types/spend-tracker";

interface EntryFormProps {
  accounts: Account[];
  onSave: (date: string, balances: Map<string, number>) => Promise<void>;
  onCancel: () => void;
  existingDates: string[];
}

const EntryForm: React.FC<EntryFormProps> = ({
  accounts,
  onSave,
  onCancel,
  existingDates,
}) => {
  const navigate = useNavigate();
  const [entryDate, setEntryDate] = useState("");
  const [balances, setBalances] = useState<Map<string, number>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const activeAccounts = accounts.filter((a) => a.isActive);
  const cashAccounts = activeAccounts.filter((a) => a.type === "cash");
  const creditAccounts = activeAccounts.filter((a) => a.type === "credit");
  const investmentAccounts = activeAccounts.filter(
    (a) => a.type === "investment"
  );

  const handleSubmit = async () => {
    if (!entryDate) {
      alert("Please select a date");
      return;
    }

    if (balances.size === 0) {
      alert("Please enter at least one balance");
      return;
    }

    if (existingDates.includes(entryDate)) {
      alert(
        "An entry already exists for this date. Please choose a different date."
      );
      return;
    }

    setIsSaving(true);
    try {
      await onSave(entryDate, balances);
    } finally {
      setIsSaving(false);
    }
  };

  const updateBalance = (accountId: string, value: string) => {
    const newBalances = new Map(balances);
    if (value === "") {
      newBalances.delete(accountId);
    } else {
      newBalances.set(accountId, parseFloat(value) || 0);
    }
    setBalances(newBalances);
  };

  const renderAccountSection = (
    title: string,
    accounts: Account[],
    hint: string
  ) => {
    if (accounts.length === 0) return null;

    return (
      <div style={{ marginBottom: "2rem" }}>
        <h3
          style={{
            fontSize: "1rem",
            fontWeight: "600",
            marginBottom: "1rem",
            color: "#374151",
          }}
        >
          {title}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {accounts.map((account) => (
            <div
              key={account.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "0.75rem",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                backgroundColor: "#fafafa",
              }}
            >
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    display: "block",
                  }}
                >
                  {account.name}
                </label>
                {account.provider && (
                  <p style={{ fontSize: "0.75rem", color: "#888" }}>
                    {account.provider}
                    {account.last4 && ` • ****${account.last4}`}
                  </p>
                )}
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={balances.get(account.id) || ""}
                  onChange={(e) => updateBalance(account.id, e.target.value)}
                  style={{
                    width: "140px",
                    padding: "0.5rem",
                    borderRadius: "0.375rem",
                    border: "1px solid #ccc",
                    textAlign: "right",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <p
          style={{
            fontSize: "0.75rem",
            color: "#6b7280",
            marginTop: "0.5rem",
            fontStyle: "italic",
          }}
        >
          {hint}
        </p>
      </div>
    );
  };

  if (activeAccounts.length === 0) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "0.5rem",
          border: "1px solid #e5e7eb",
          padding: "1.5rem",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: "600",
            marginBottom: "1rem",
          }}
        >
          Create New Entry
        </h2>
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            backgroundColor: "#fef3c7",
            borderRadius: "0.5rem",
            border: "1px solid #fcd34d",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              color: "#92400e",
              marginBottom: "1rem",
            }}
          >
            ⚠️ No active accounts found. Please add accounts first.
          </p>
          <button
            onClick={() => navigate("/money/accounts")}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              backgroundColor: "#2563eb",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Go to Accounts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#fff",
        borderRadius: "0.5rem",
        border: "1px solid #e5e7eb",
        padding: "1.5rem",
      }}
    >
      <h2
        style={{
          fontSize: "1.25rem",
          fontWeight: "600",
          marginBottom: "1rem",
        }}
      >
        Create New Entry
      </h2>

      <div style={{ marginBottom: "2rem" }}>
        <label
          style={{
            display: "block",
            fontSize: "0.875rem",
            fontWeight: "500",
            marginBottom: "0.5rem",
          }}
        >
          Entry Date
        </label>
        <input
          type="date"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
          style={{
            padding: "0.5rem",
            borderRadius: "0.375rem",
            border: "1px solid #ccc",
            fontSize: "1rem",
          }}
        />
      </div>

      {renderAccountSection(
        "💰 Checking & Savings",
        cashAccounts,
        "Enter positive values for cash balances"
      )}

      {renderAccountSection(
        "💳 Credit Cards",
        creditAccounts,
        "Enter negative values (e.g., -1,200.50 for a balance you owe)"
      )}

      {renderAccountSection(
        "📈 Investments",
        investmentAccounts,
        "Enter current market value of your investment accounts"
      )}

      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop: "2rem",
          paddingTop: "1.5rem",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          style={{
            padding: "0.75rem 1.5rem",
            borderRadius: "0.375rem",
            backgroundColor: isSaving ? "#9ca3af" : "#2563eb",
            color: "white",
            border: "none",
            cursor: isSaving ? "not-allowed" : "pointer",
            fontWeight: "500",
            flex: 1,
          }}
        >
          {isSaving ? "Saving..." : "Save Entry"}
        </button>
        <button
          onClick={onCancel}
          disabled={isSaving}
          style={{
            padding: "0.75rem 1.5rem",
            borderRadius: "0.375rem",
            backgroundColor: "white",
            color: "#374151",
            border: "1px solid #d1d5db",
            cursor: isSaving ? "not-allowed" : "pointer",
            fontWeight: "500",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default EntryForm;
